import { useState, useEffect, useCallback } from "react";
import { addDays, format } from "date-fns";
import { X, AlertCircle } from "lucide-react";
import type { Challenge, ChallengeTemplate, ChallengeType, ChallengeMetric, RecurrenceUnit } from "../types";
import {
  CHALLENGE_TYPES,
  CHALLENGE_TYPE_LABEL_KEYS,
  CHALLENGE_METRICS,
  CHALLENGE_METRIC_LABEL_KEYS,
  RECURRENCE_UNITS,
  RECURRENCE_UNIT_LABEL_KEYS,
} from "../constants";
import { ImageUploader } from "../../marketplace/components/ImageUploader";
import { useLocale } from "../../../context/LanguageContext";

// Re-export for convenience
type ImageFile = File | null;

// ─── Form State ──────────────────────────────────────────────────────────────

interface RecurrenceState {
  enabled: boolean;
  intervalValue: string;
  intervalUnit: RecurrenceUnit;
}

interface FormState {
  name: string;
  description: string;
  type: ChallengeType;
  metric: ChallengeMetric;
  targetValue: string;
  startDate: string;
  endDate: string;
  entryFee: string;
  recurrence: RecurrenceState;
  rewards: Array<{ fromRank: string; toRank: string; description: string }>;
}

function getInitialState(challenge?: Challenge): FormState {
  if (challenge) {
    return {
      name: challenge.name,
      description: challenge.description ?? "",
      type: challenge.type,
      metric: challenge.metric,
      targetValue: challenge.targetValue ? String(challenge.targetValue) : "",
      startDate: challenge.startDate.slice(0, 10),
      endDate: challenge.endDate.slice(0, 10),
      entryFee: String(challenge.entryFee ?? 0),
      recurrence: {
        enabled: challenge.recurrence?.enabled ?? false,
        intervalValue: String(challenge.recurrence?.intervalValue ?? 1),
        intervalUnit: challenge.recurrence?.intervalUnit ?? "MONTHS",
      },
      rewards:
        challenge.rewards?.map((r) => ({
          fromRank: String(r.fromRank),
          toRank: String(r.toRank),
          description: r.description,
        })) ?? [],
    };
  }
  return {
    name: "",
    description: "",
    type: "BEST_YOU_CAN",
    metric: "STEPS",
    targetValue: "",
    startDate: "",
    endDate: "",
    entryFee: "0",
    recurrence: { enabled: false, intervalValue: "1", intervalUnit: "MONTHS" },
    rewards: [],
  };
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface CreateChallengeDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>, imageFile?: File | null) => void;
  loading: boolean;
  challenge?: Challenge;
  duplicateFrom?: Challenge;
  templateFill?: ChallengeTemplate | null;
  currentOrgId: string | null;
  successView?: React.ReactNode;
  error?: string | null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CreateChallengeDrawer({
  isOpen,
  onClose,
  onSubmit,
  loading,
  challenge,
  duplicateFrom,
  templateFill,
  currentOrgId,
  successView,
  error,
}: CreateChallengeDrawerProps) {
  const [form, setForm] = useState<FormState>(getInitialState(challenge));
  const [imageFile, setImageFile] = useState<ImageFile>(null);
  const { t } = useLocale();

  const isEdit = Boolean(challenge);

  // Re-init form when item or open state changes
  useEffect(() => {
    if (isOpen) {
      if (duplicateFrom) {
        const state = getInitialState(duplicateFrom);
        state.name = `${duplicateFrom.name}${t("common.copySuffix")}`;
        state.startDate = "";
        state.endDate = "";
        setForm(state);
      } else {
        setForm(getInitialState(challenge));
      }
      setImageFile(null);
    }
  }, [isOpen, challenge, duplicateFrom, t]);

  // Apply template fill
  useEffect(() => {
    if (templateFill) {
      const startDate = addDays(new Date(), 1);
      const endDate = addDays(startDate, templateFill.durationDays);
      setForm((prev) => ({
        ...prev,
        name: templateFill.name,
        description: templateFill.description,
        type: templateFill.type,
        metric: templateFill.metric,
        targetValue: templateFill.targetValue ? String(templateFill.targetValue) : "",
        startDate: format(startDate, "yyyy-MM-dd"),
        endDate: format(endDate, "yyyy-MM-dd"),
        entryFee: String(templateFill.entryFee),
        recurrence: {
          enabled: templateFill.recurrence.enabled,
          intervalValue: String(templateFill.recurrence.intervalValue),
          intervalUnit: templateFill.recurrence.intervalUnit,
        },
        rewards: templateFill.rewards.map((r) => ({
          fromRank: String(r.fromRank),
          toRank: String(r.toRank),
          description: r.description,
        })),
      }));
    }
  }, [templateFill]);

  const set = useCallback(
    (field: keyof FormState, value: string | boolean) =>
      setForm((prev) => ({ ...prev, [field]: value })),
    [],
  );

  const handleImageSelect = useCallback((file: File) => {
    setImageFile(file);
  }, []);

  const handleImageRemove = useCallback(() => {
    setImageFile(null);
  }, []);

  // ─── Rewards Management ────────────────────────────────────────────────

  const addReward = useCallback(() => {
    setForm((prev) => {
      const lastReward = prev.rewards[prev.rewards.length - 1];
      const nextFrom = lastReward
        ? String(parseInt(lastReward.toRank, 10) + 1)
        : "1";
      return {
        ...prev,
        rewards: [
          ...prev.rewards,
          { fromRank: nextFrom, toRank: nextFrom, description: "" },
        ],
      };
    });
  }, []);

  const removeReward = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      rewards: prev.rewards.filter((_, i) => i !== index),
    }));
  }, []);

  const updateReward = useCallback(
    (index: number, field: string, value: string) => {
      setForm((prev) => ({
        ...prev,
        rewards: prev.rewards.map((r, i) =>
          i === index ? { ...r, [field]: value } : r,
        ),
      }));
    },
    [],
  );

  // ─── Submit ────────────────────────────────────────────────────────────

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const input: Record<string, unknown> = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      startDate: new Date(`${form.startDate}T00:00`).toISOString(),
      endDate: new Date(`${form.endDate}T23:59:59.999`).toISOString(),
    };

    // entryFee is allowed for both create and update (draft-only guard on backend)
    input.entryFee = form.entryFee === "" ? 0 : parseInt(form.entryFee, 10) || 0;

    // Fields only valid for creation (not in UpdateChallengeInput)
    if (!isEdit) {
      input.type = form.type;
      input.metric = form.metric;

      if (form.type === "HIT_THE_TARGET" && form.targetValue) {
        input.targetValue = parseFloat(form.targetValue);
      }
    }

    if (form.rewards.length > 0) {
      input.rewards = form.rewards
        .filter((r) => r.description.trim())
        .map((r) => ({
          fromRank: parseInt(r.fromRank, 10),
          toRank: parseInt(r.toRank, 10),
          description: r.description.trim(),
        }));
    }

    // Recurrence
    input.recurrence = {
      enabled: form.recurrence.enabled,
      intervalValue: parseInt(form.recurrence.intervalValue, 10) || 1,
      intervalUnit: form.recurrence.intervalUnit,
    };

    // Auto-set org-scoped accessibility
    if (!isEdit) {
      input.accessibility = "ORG_ONLY";
      if (currentOrgId) {
        input.organizationId = currentOrgId;
      }
    }

    onSubmit(input, imageFile);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col bg-card shadow-xl">
        {successView ? (
          successView
        ) : (
          <>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            {isEdit
              ? t("challenges.drawer.editTitle")
              : duplicateFrom
                ? t("challenges.drawer.duplicateTitle")
                : t("challenges.drawer.createTitle")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:bg-muted"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form
          id="challenge-drawer-form"
          onSubmit={handleSubmit}
          className="flex-1 space-y-4 overflow-y-auto px-6 py-4"
        >
          {/* Name */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t("challenges.drawer.titleLabel")} *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              maxLength={100}
              required
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={t("adminUI.challenges.createNamePlaceholder")}
            />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t("challenges.drawer.description")}
            </label>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              maxLength={200}
              rows={3}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={t("adminUI.challenges.createDescriptionPlaceholder")}
            />
          </div>

          {/* Cover Image */}
          <div>
            <ImageUploader
              label={t("adminUI.imageUploader.coverImage")}
              currentImageUrl={challenge?.photoUrl}
              onFileSelect={handleImageSelect}
              onRemove={handleImageRemove}
            />
          </div>

          {/* Type + Metric row */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("challenges.drawer.type")} *
              </label>
              <select
                value={form.type}
                onChange={(e) =>
                  set("type", e.target.value)
                }
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {CHALLENGE_TYPES.map((ct) => (
                  <option key={ct} value={ct}>
                    {t(CHALLENGE_TYPE_LABEL_KEYS[ct])}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("challenges.drawer.metric")} *
              </label>
              <select
                value={form.metric}
                onChange={(e) =>
                  set("metric", e.target.value)
                }
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {CHALLENGE_METRICS.map((m) => (
                  <option key={m} value={m}>
                    {t(CHALLENGE_METRIC_LABEL_KEYS[m])}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Target Value (conditional) */}
          {form.type === "HIT_THE_TARGET" && (
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("challenges.drawer.targetValue")} *
              </label>
              <input
                type="number"
                value={form.targetValue}
                onChange={(e) => set("targetValue", e.target.value)}
                min={1}
                step="any"
                required
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="10000"
              />
            </div>
          )}

          {/* Date row */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("challenges.drawer.startDate")} *
              </label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => set("startDate", e.target.value)}
                required
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("challenges.drawer.endDate")} *
              </label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => set("endDate", e.target.value)}
                required
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {/* Entry Fee */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t("challenges.drawer.entryFee")}
            </label>
            <input
              type="number"
              value={form.entryFee}
              onChange={(e) => set("entryFee", e.target.value)}
              onFocus={() => {
                if (form.entryFee === "0") set("entryFee", "");
              }}
              onBlur={() => {
                if (form.entryFee === "") set("entryFee", "0");
              }}
              min={0}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={t("challenges.drawer.entryFeePlaceholder")}
            />
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("challenges.drawer.entryFeeHint")}
            </p>
          </div>

          {/* Rewards */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                {t("challenges.drawer.rewards")}
              </label>
              <button
                type="button"
                onClick={addReward}
                className="text-xs font-medium text-primary hover:underline"
              >
                + {t("challenges.drawer.addTier")}
              </button>
            </div>
            {form.rewards.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {t("challenges.drawer.noRewards")}
              </p>
            )}
            <div className="space-y-2">
              {form.rewards.map((reward, i) => (
                <div key={i} className="flex items-start gap-2">
                  <input
                    type="number"
                    value={reward.fromRank}
                    onChange={(e) =>
                      updateReward(i, "fromRank", e.target.value)
                    }
                    min={1}
                    placeholder={t("adminUI.challenges.createDateFrom")}
                    className="w-16 rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground"
                  />
                  <span className="mt-1.5 text-xs text-muted-foreground">–</span>
                  <input
                    type="number"
                    value={reward.toRank}
                    onChange={(e) =>
                      updateReward(i, "toRank", e.target.value)
                    }
                    min={1}
                    placeholder={t("adminUI.challenges.createDateTo")}
                    className="w-16 rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground"
                  />
                  <input
                    type="text"
                    value={reward.description}
                    onChange={(e) =>
                      updateReward(i, "description", e.target.value)
                    }
                    maxLength={200}
                    placeholder={t("challenges.drawer.rewardPlaceholder")}
                    className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground"
                  />
                  <button
                    type="button"
                    onClick={() => removeReward(i)}
                    className="mt-1 rounded p-0.5 text-destructive hover:bg-destructive/10"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Autopilot: Recurrence */}
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={form.recurrence.enabled}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    recurrence: { ...prev.recurrence, enabled: e.target.checked },
                  }))
                }
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <div>
                <span className="text-sm font-medium text-foreground">
                  {t("challenges.drawer.recurring")}
                </span>
                <p className="text-xs text-muted-foreground">
                  {t("challenges.drawer.recurringHint")}
                </p>
              </div>
            </label>

            <div className={`mt-3 flex items-center gap-2 transition-opacity ${!form.recurrence.enabled ? 'opacity-40 pointer-events-none' : ''}`}>
              <span className="text-sm text-muted-foreground">{t("challenges.drawer.every")}</span>
              <input
                type="number"
                value={form.recurrence.intervalValue}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    recurrence: { ...prev.recurrence, intervalValue: e.target.value },
                  }))
                }
                min={1}
                max={365}
                disabled={!form.recurrence.enabled}
                aria-disabled={!form.recurrence.enabled}
                tabIndex={form.recurrence.enabled ? 0 : -1}
                className="w-20 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed"
              />
              <select
                value={form.recurrence.intervalUnit}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    recurrence: {
                      ...prev.recurrence,
                      intervalUnit: e.target.value as RecurrenceUnit,
                    },
                  }))
                }
                disabled={!form.recurrence.enabled}
                aria-disabled={!form.recurrence.enabled}
                tabIndex={form.recurrence.enabled ? 0 : -1}
                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed"
              >
                {RECURRENCE_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {t(RECURRENCE_UNIT_LABEL_KEYS[u])}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </form>

        {/* Error Banner */}
        {error && (
          <div className="mx-6 mb-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="inline-flex h-9 items-center rounded-md border border-border px-4 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="challenge-drawer-form"
            disabled={loading}
            className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading
              ? t("challenges.drawer.saving")
              : isEdit
                ? t("challenges.drawer.update")
                : t("challenges.drawer.createDraft")}
          </button>
        </div>
          </>
        )}
      </div>
    </>
  );
}
